import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SelectAllDropdown from './SelectAllDropdown/SelectAllDropdown';
import ConfirmationModal from '../../../../../components/ConfirmationModal/ConfirmationModal';
import { Result } from '../../../../../services/serpApi'; // Importando a interface centralizada
import ResultsPaginationTop from './ResultsPaginationTop/ResultsPaginationTop';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { exportToCsv } from '../../../../../services/exportService';
import toast from 'react-hot-toast';
import FilterDropdown from '../../../../../components/FilterDropdown/FilterDropdown'; // Importando o dropdown reutilizável
import { SearchControls } from '../../SearchSection/SearchSection'; // Importando SearchControls

interface ResultsHeaderProps {
  totalResults: number;
  selectedResults: number[];
  setSelectedResults: (ids: number[]) => void;
  allResults: Result[];
  onDeleteResults: (ids: number[]) => void;
  onSaveSelectedResults: () => Promise<void>;
  onVerifySelectedNumbers: () => Promise<void>;
  isVerifying: boolean;
  isWhatsappConnected: boolean;

  // Props de Paginação
  currentPage: number;
  resultsPerPage: number;
  onPageChange: (page: number) => void;
  hasMoreChunks: boolean;

  // NEW: Props de controle de filtros
  currentControls: SearchControls;
  onUpdateControls: (newControls: SearchControls) => void;
}

const ResultsHeader: React.FC<ResultsHeaderProps> = ({
  totalResults,
  selectedResults,
  setSelectedResults,
  allResults,
  onDeleteResults,
  onSaveSelectedResults,
  onVerifySelectedNumbers,
  isVerifying,
  isWhatsappConnected,

  // Paginação Props
  currentPage,
  resultsPerPage,
  onPageChange,
  hasMoreChunks,

  // NEW: Props de controle
  currentControls,
  onUpdateControls,
}) => {
  const { t } = useTranslation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null); // Estado para gerenciar dropdowns

  const handleSaveSelected = async () => {
    setIsSaving(true);
    await onSaveSelectedResults();
    setIsSaving(false);
  };

  const handleExportSelected = () => {
    if (selectedResults.length === 0) {
      toast.error(t('Selecione pelo menos um resultado para exportar.'));
      return;
    }

    const leadsToExport = allResults.filter(r => selectedResults.includes(r.id));

    try {
      exportToCsv(leadsToExport, 'mi_buscador_dashboard_export.csv');
      toast.success(t('Exportação iniciada! Verifique seus downloads.'));
    } catch (error) {
      toast.error(t('Falha ao exportar para CSV.'));
    }
  };

  const handleConfirmDelete = () => {
    onDeleteResults(selectedResults);
    setIsDeleteModalOpen(false);
  };

  // Total de resultados no chunk atual (para a paginação do topo)
  const totalResultsInChunk = allResults.length;

  // 1. Verifica se há leads selecionados que têm telefone e não foram validados
  const hasUnverifiedSelected = allResults.some(r =>
    selectedResults.includes(r.id) && r.status !== 'Validado' && r.phone
  );

  // 2. Condição final para desabilitar o botão
  const isVerifyDisabled = selectedResults.length === 0 || isVerifying || !hasUnverifiedSelected || !isWhatsappConnected;

  // 3. Tooltip
  const verifyTooltip = !isWhatsappConnected
    ? t('Por favor, conecte sua instância do WhatsApp na aba Integrações.')
    : selectedResults.length === 0
      ? t('Selecione leads com números de telefone para verificar.')
      : !hasUnverifiedSelected
        ? t('Todos os leads selecionados já foram verificados ou não possuem telefone.')
        : t('Verificar se os números selecionados possuem WhatsApp ativo.');

  // --- Lógica dos Filtros (Movida de FilterChips) ---

  // --- WhatsApp Filter ---
  const whatsappOptions = [
    { label: t('dashboard.filter_whatsapp_default'), value: 'all' },
    { label: t('dashboard.filter_whatsapp_verified'), value: 'verified' },
    { label: t('dashboard.filter_whatsapp_unverified_pending'), value: 'unverified_pending' },
  ];

  const handleWhatsappSelect = (label: string) => {
    const value = whatsappOptions.find(o => o.label === label)?.value || 'all';
    onUpdateControls({ ...currentControls, whatsappStatus: value as SearchControls['whatsappStatus'] });
    setOpenDropdown(null);
  };

  const getWhatsappLabel = (value: string) => {
    return whatsappOptions.find(o => o.value === value)?.label || t('dashboard.filter_whatsapp_default');
  };

  // --- Rating Filter (Stars) ---
  const ratingOptions = [5, 4, 3, 2, 1, 0]; // Adicionando 0 para limpar

  const handleRatingSelect = (label: string) => {
    // Extrai o número da string traduzida (ex: '5 Estrelas' -> 5)
    const value = parseInt(label.split(' ')[0]) || 0; // Se for 'Avaliação' ou 'Limpar', retorna 0
    onUpdateControls({ ...currentControls, rating: value });
    setOpenDropdown(null);
  };

  const getRatingLabel = (value: number) => {
    if (value === 0) return t('dashboard.filter_rating_default');
    const starText = t('Estrela', { count: value });
    return `${value} ${starText}`;
  };

  // --- Sort By Dropdown ---
  const sortOptions = [
    { label: t('dashboard.filter_sort_relevance'), value: 'relevance' },
    { label: t('dashboard.filter_sort_name_asc'), value: 'name_asc' },
    { label: t('dashboard.filter_sort_name_desc'), value: 'name_desc' },
    { label: t('dashboard.filter_sort_rating_desc'), value: 'rating_desc' },
    { label: t('dashboard.filter_sort_rating_asc'), value: 'rating_asc' },
  ];

  const handleSortSelect = (label: string) => {
    const value = sortOptions.find(o => o.label === label)?.value || 'relevance';
    onUpdateControls({ ...currentControls, sortBy: value as SearchControls['sortBy'] });
    setOpenDropdown(null);
  };

  const getSortLabel = (value: string) => {
    return sortOptions.find(o => o.value === value)?.label || t('dashboard.filter_sort_relevance');
  };

  const handleClearFilters = () => {
    const defaultControls: SearchControls = {
      rating: 0,
      whatsappStatus: 'all',
      sortBy: 'relevance',
      resultsPerPage: currentControls.resultsPerPage, // Mantém o resultsPerPage
    };
    onUpdateControls(defaultControls);
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        {/* Linha 1: Título e Paginação Topo */}
        <div className="flex justify-between items-center flex-wrap gap-4">
          <motion.h3
            className="text-lg font-bold text-[#181411] dark:text-white"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            {t('dashboard.results_title', { totalResults })}
          </motion.h3>

          {/* Paginação no Topo */}
          {totalResultsInChunk > 0 && (
            <ResultsPaginationTop
              currentPage={currentPage}
              totalResults={totalResultsInChunk}
              resultsPerPage={resultsPerPage}
              onPageChange={onPageChange}
              hasMoreChunks={hasMoreChunks}
            />
          )}
        </div>

        {/* Linha 2: Filtros e Ações */}
        <div className="flex items-center gap-3 flex-wrap">

          {/* Clear Filters Button */}
          <motion.button
            onClick={handleClearFilters}
            className="flex h-9 shrink-0 items-center justify-center gap-x-2 rounded-lg bg-gray-100 dark:bg-gray-900/50 px-3 hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-colors text-sm text-gray-600 dark:text-gray-400"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="material-symbols-outlined text-lg">close</span>
            {t('dashboard.filter_clear')}
          </motion.button>

          {/* WhatsApp Status Filter */}
          <FilterDropdown
            type="whatsappStatus"
            label={getWhatsappLabel(currentControls.whatsappStatus)}
            options={whatsappOptions.map(o => o.label)}
            selectedValue={getWhatsappLabel(currentControls.whatsappStatus)}
            onSelect={handleWhatsappSelect}
            isOpen={openDropdown === 'whatsappStatus'}
            onToggle={() => setOpenDropdown(openDropdown === 'whatsappStatus' ? null : 'whatsappStatus')}
          />

          {/* Rating Filter (Stars) */}
          <FilterDropdown
            type="rating"
            label={getRatingLabel(currentControls.rating)}
            options={ratingOptions.map(r => r === 0 ? t('dashboard.filter_rating_default') : t('{{count}} Estrela', { count: r, defaultValue: `${r} Estrela${r > 1 ? 's' : ''}` }))}
            selectedValue={getRatingLabel(currentControls.rating)}
            onSelect={handleRatingSelect}
            isOpen={openDropdown === 'rating'}
            onToggle={() => setOpenDropdown(openDropdown === 'rating' ? null : 'rating')}
          />

          {/* Sort By Dropdown */}
          <FilterDropdown
            type="sortBy"
            label={getSortLabel(currentControls.sortBy)}
            options={sortOptions.map(o => o.label)}
            selectedValue={getSortLabel(currentControls.sortBy)}
            onSelect={handleSortSelect}
            isOpen={openDropdown === 'sortBy'}
            onToggle={() => setOpenDropdown(openDropdown === 'sortBy' ? null : 'sortBy')}
          />

          <div className="flex-grow"></div>

          {/* Ações em Massa */}
          {/* Ações em Massa */}
          <motion.div
            className="flex flex-col md:flex-row items-stretch md:items-center gap-2 w-full md:w-auto"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="md:w-auto w-full">
              <SelectAllDropdown
                allResults={allResults}
                selectedResults={selectedResults}
                setSelectedResults={setSelectedResults}
              />
            </div>

            {/* Botão de Verificar WhatsApp */}
            <Link
              to={!isWhatsappConnected ? '/integracoes' : '#'}
              title={verifyTooltip}
              className="md:w-auto w-full"
              onClick={(e) => {
                if (!isWhatsappConnected) {
                  e.preventDefault();
                } else if (isVerifyDisabled) {
                  e.preventDefault();
                }
              }}
            >
              <motion.button
                onClick={onVerifySelectedNumbers}
                className="flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium text-green-600 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
                whileHover={{ scale: isVerifyDisabled ? 1 : 1.05 }}
                whileTap={{ scale: isVerifyDisabled ? 1 : 0.95 }}
                disabled={isVerifyDisabled}
              >
                {isVerifying ? (
                  <span className="material-symbols-outlined text-lg animate-spin">sync</span>
                ) : (
                  <span className="material-symbols-outlined text-lg">check_circle</span>
                )}
                {isVerifying ? t('dashboard.verifying') : t('dashboard.action_verify')} ({selectedResults.length})
              </motion.button>
            </Link>

            {/* Botão de Excluir */}
            <motion.button
              onClick={() => setIsDeleteModalOpen(true)}
              className="flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium text-red-600 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
              whileHover={{ scale: selectedResults.length === 0 ? 1 : 1.05 }}
              whileTap={{ scale: selectedResults.length === 0 ? 1 : 0.95 }}
              disabled={selectedResults.length === 0 || isSaving || isVerifying}
            >
              <span className="material-symbols-outlined text-lg">delete</span>
              {t('dashboard.action_delete')} ({selectedResults.length})
            </motion.button>

            {/* Botão de Salvar */}
            <motion.button
              onClick={handleSaveSelected}
              className="flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-white bg-gray-100 dark:bg-background-dark-elevated2 hover:bg-gray-200 dark:hover:bg-background-dark-elevated3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-transparent dark:border-primary-darkMode w-full md:w-auto"
              whileHover={{ scale: selectedResults.length === 0 ? 1 : 1.05 }}
              whileTap={{ scale: selectedResults.length === 0 ? 1 : 0.95 }}
              disabled={selectedResults.length === 0 || isSaving || isVerifying}
            >
              {isSaving ? (
                <span className="material-symbols-outlined text-lg animate-spin">sync</span>
              ) : (
                <span className="material-symbols-outlined text-lg">save</span>
              )}
              {isSaving ? t('dashboard.loading') : `${t('dashboard.action_save')} (${selectedResults.length})`}
            </motion.button>

            <motion.button
              onClick={handleExportSelected}
              className="flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-white bg-gray-100 dark:bg-background-dark-elevated2 hover:bg-gray-200 dark:hover:bg-background-dark-elevated3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-transparent dark:border-primary-darkMode w-full md:w-auto"
              whileHover={{ scale: selectedResults.length === 0 ? 1 : 1.05 }}
              whileTap={{ scale: selectedResults.length === 0 ? 1 : 0.95 }}
              disabled={selectedResults.length === 0 || isSaving || isVerifying}
            >
              <span className="material-symbols-outlined text-lg">download</span>
              {t('dashboard.action_export')} ({selectedResults.length})
            </motion.button>
          </motion.div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('modals.confirm_delete_title')}
        message={t('Você tem certeza que deseja excluir {{count}} resultado(s) selecionado(s)? Esta ação não pode ser desfeita.', { count: selectedResults.length })}
        confirmText={t('Sim, Excluir Permanentemente')}
        cancelText={t('modals.cancel')}
      />
    </>
  );
};

export default ResultsHeader;